import { useState } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';

// Icons
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

import { container } from '../../pages/ProposalView';

const isImage = (filename) => {
  if (!filename) return false;
  return /\.(png|jpe?g|gif|webp|heic)$/i.test(filename);
};

const isPdf = (filename) => {
  if (!filename) return false;
  return /\.pdf$/i.test(filename);
};

const ProposalViewAdditionalInfo = ({ info }) => {
  const [previewFile, setPreviewFile] = useState(null);

  const handlePreview = (file) => {
    if (isImage(file.filename) || isPdf(file.filename)) {
      setPreviewFile(file);
    } else {
      // For other file types, open in new tab as before
      window.open(file.url, '_blank');
    }
  };

  const handleClose = () => {
    setPreviewFile(null);
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box component="section" sx={container}>
      <Typography variant="h5" sx={{ pt: 4, pb: 2 }}>
        Additional information
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell width="5%">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {info?.map((el) => (
              <TableRow key={el.id}>
                <TableCell scope="row">{el.filename}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="View">
                      <IconButton onClick={() => handlePreview(el)} size="small">
                        <RemoveRedEyeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton onClick={() => handleDownload(el)} size="small">
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Preview Dialog */}
      <Dialog
        open={!!previewFile}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '95vh', md: '90vh' },
            maxHeight: '95vh',
            m: { xs: 1, md: 4 },
          }
        }}
      >
        {previewFile && (
          <>
            <DialogTitle sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: { xs: 1.5, md: 2 },
              borderBottom: '1px solid #e0e0e0',
            }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: 13, md: 16 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  mr: 1,
                }}
              >
                {previewFile.filename}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title="Download">
                  <IconButton onClick={() => handleDownload(previewFile)} size="small">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto', backgroundColor: '#f5f5f5' }}>
              {isImage(previewFile.filename) && (
                <img
                  src={previewFile.url}
                  alt={previewFile.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              )}
              {isPdf(previewFile.filename) && (
                <iframe
                  src={previewFile.url}
                  title={previewFile.filename}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProposalViewAdditionalInfo;
